import React, { useState } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { X, Calendar, AlertTriangle, Check } from 'lucide-react';
import Chip from '../../../Shared/ui/Chip';
import { costBundleTypes } from '../../../../Config/constants';
import { parsePrice } from '../../../../Utils/priceInput';
import Button from '../../../Shared/Buttons/Button';

export default function EditCostBundleDialog({ 
    dialogOpen, 
    closeDialog, 
    rowId, 
    newPriceBundleId, 
    oldPriceBundleId,
    rowDescription,
    allPriceBundles,
    currency = { symbol: "€" },
    onSave,
    ...props 
}) {
    const classes = useStyles();
    
    const [generalError, setGeneralError] = useState(null);
    const [historicalChangeDate, setHistoricalChangeDate] = useState(new Date());
    const [applyHistoricalChange, setApplyHistoricalChange] = useState(false);

    const handleCloseDialog = () => {
        setGeneralError(null);
        setApplyHistoricalChange(false);
        setHistoricalChangeDate(new Date());
        closeDialog();
    };

    const handleSave = () => {
        setGeneralError(null);
        
        if (applyHistoricalChange && !historicalChangeDate) {
            setGeneralError("Please select a start date for historical changes.");
            return;
        }

        // Call the parent's save function with the selected options
        onSave(rowId, newPriceBundleId, applyHistoricalChange ? historicalChangeDate : null);
        handleCloseDialog();
    };

    const getTotalBundlePrice = (priceBundleItems) => {
        // sum of all priceBundleItem.price * priceBundleItem.multiplier
        if (priceBundleItems && priceBundleItems.length > 0) {
            return (
                currency.symbol +
                priceBundleItems
                    .reduce((total, item) => {
                        const price = parsePrice(item.price) ?? 0;
                        return total + price * (item.multiplier || 1);
                    }, 0)
                    .toFixed(3)
            );
        }
        return "0.00 " + currency.symbol;
    };

    const formatPriceBundle = (priceBundleId) => {
        if (!priceBundleId || !allPriceBundles || allPriceBundles.length === 0) {
            return <span className={classes.noBundleText}>None</span>;
        }
        
        const priceBundle = allPriceBundles.find(pb => pb.id === priceBundleId);
        if (!priceBundle) {
            return <span className={classes.noBundleText}>None</span>;
        }

        // Determine pricing type suffix based on costBundleTypeId
        const pricingTypeSuffix = priceBundle.costBundleTypeId === costBundleTypes.TIME ? "/hour" : "/event";

        return (
            <div className={classes.bundleInfo}>
                <span className={classes.bundleName}>{priceBundle.reference} - {priceBundle.name}</span>
                <Chip size="extraSmall" variant={priceBundle.account.isRevenue ? "success" : "danger"}>
                    {getTotalBundlePrice(priceBundle.priceBundleItems)}
                    {pricingTypeSuffix}
                </Chip>
            </div>
        );
    };

    const getOldPriceBundleElement = () => {
        return formatPriceBundle(oldPriceBundleId);
    };

    const getNewPriceBundleElement = () => {
        return formatPriceBundle(newPriceBundleId);
    };

    return (
        <Dialog 
            className={classes.dialog}
            open={dialogOpen}
            aria-labelledby="edit-cost-bundle-dialog-title"
            onClose={handleCloseDialog}
            classes={{ paper: classes.dialogPaper }}
        >
            <div className={classes.dialog__head}>
                <DialogTitle className={classes.dialog__title} id="edit-cost-bundle-dialog-title">
                    Edit Cost Bundle
                </DialogTitle>
                <button onClick={handleCloseDialog} className={classes.dialog__closeButton}>
                    <X size={19} />
                </button>
            </div>
            
            <DialogContent className={classes.dialog__contentContainer}>
                <div className={classes.dialog__content}>
                    {generalError && (
                        <div className={classes.errorContainer}>
                            <p className={classes.errorMessage}>{generalError}</p>
                        </div>
                    )}
                    
                    <div className={classes.warningContainer}>
                        <div className={classes.warningHeader}>
                            <AlertTriangle className={classes.warningIcon} />
                            <p className={classes.warningTitle}>Measurement Point Has Existing Data</p>
                        </div>
                        <p className={classes.warningMessage}>
                            You are changing the cost bundle for "<strong>{rowDescription}</strong>" which already has measurements. 
                            This change will affect how the price of future measurements is calculated.
                        </p>
                    </div>

                    <div className={classes.changeOverview}>
                        <div className={classes.changeItem}>
                            <span className={classes.changeLabel}>From:</span>
                            <div className={classes.changeValue}>{getOldPriceBundleElement()}</div>
                        </div>
                        <div className={classes.changeItem}>
                            <span className={classes.changeLabel}>To:</span>
                            <div className={classes.changeValue}>{getNewPriceBundleElement()}</div>
                        </div>
                    </div>

                    <div className={classes.optionsContainer}>
                        <p className={classes.optionsTitle}>What would you like to do?</p>
                        
                        <div className={classes.optionGroup}>
                            <div className={classes.radioOption} onClick={() => setApplyHistoricalChange(false)}>
                                <div className={`${classes.radioIndicator} ${!applyHistoricalChange ? classes.radioIndicatorSelected : classes.radioIndicatorUnselected}`}>
                                    {!applyHistoricalChange && <Check className={classes.radioCheckIcon} />}
                                </div>
                                <div className={classes.radioContent}>
                                    <span className={classes.radioTitle}>Apply from now on</span>
                                    <span className={classes.radioDescription}>
                                        Only new measurements will use the new cost bundle. Existing measurements keep their original pricing.
                                    </span>
                                </div>
                            </div>

                            <div className={classes.radioOption} onClick={() => setApplyHistoricalChange(true)}>
                                <div className={`${classes.radioIndicator} ${applyHistoricalChange ? classes.radioIndicatorSelected : classes.radioIndicatorUnselected}`}>
                                    {applyHistoricalChange && <Check className={classes.radioCheckIcon} />}
                                </div>
                                <div className={classes.radioContent}>
                                    <span className={classes.radioTitle}>Apply historical changes</span>
                                    <span className={classes.radioDescription}>
                                        Update pricing for existing measurements from a specific date onwards.
                                    </span>
                                </div>
                            </div>
                        </div>

                        {applyHistoricalChange && (
                            <div className={classes.datePickerContainer}>
                                <label className={classes.datePickerLabel}>
                                    <Calendar className={classes.datePickerIcon} />
                                    Apply changes from:
                                </label>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        value={historicalChangeDate}
                                        onChange={(date) => setHistoricalChangeDate(date)}
                                        format="dd/MM/yyyy"
                                        slotProps={{
                                            textField: {
                                                className: classes.datePicker,
                                                placeholder: "Select start date"
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
            
            <div className={classes.dialog__actions}>
                <Button variant='outlined' onClick={handleCloseDialog}>
                    Cancel
                </Button>
                <Button onClick={handleSave}>
                    {applyHistoricalChange ? 'Apply Changes' : 'Apply from Now On'}
                </Button>
            </div>
        </Dialog>
    );
}

const useStyles = makeStyles(() => ({
    dialog: {
        margin: "auto"
    },

    dialogPaper: {
        width: '520px !important',
        height: 'auto',
        borderRadius: '6px !important',
    },

    dialog__head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '16px 16px 0px 16px !important',
        marginBottom: '8px !important',
    },

    dialog__title: {
        fontSize: '24px !important',
        fontFamily: "Lato, sans-serif !important",
        fontWeight: 'normal',
        lineHeight: '29px !important',
        padding: '0px !important',
    },

    dialog__closeButton: {
        backgroundColor: '#F5F5F5',
        border: 'none',
        cursor: 'pointer',
        padding: '3px !important',
        height: '25px !important',
        borderRadius: '3px',
        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#D84949',
            color: '#F5F5F5',
        },

        '&:focus-visible': {
            outline: 'var(--color-primary) auto 1px',
        },
    },

    dialog__contentContainer: {
        padding: '0px 16px !important',
        marginBottom: '24px',

        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
    },

    dialog__content: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        margin: '0px !important',
    },

    dialog__actions: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '0px 16px 16px 16px !important',
    },

    errorContainer: {
        width: '100%',
        backgroundColor: '#FDF3F3',
        border: '1px solid #FBE5E5',
        borderRadius: '6px',
        marginBottom: '16px',
        padding: '12px',
    },

    errorMessage: {
        fontSize: '16px',
        color: '#D84949',
        margin: '0',
    },

    warningContainer: {
        width: '100%',
        backgroundColor: '#FFF8E1',
        border: '1px solid #FFE082',
        borderRadius: '6px',
        marginBottom: '16px',
        padding: '12px',
    },

    warningHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    },

    warningIcon: {
        color: '#FF8F00',
        height: '20px',
        width: '20px',
    },

    warningTitle: {
        fontSize: '16px',
        fontWeight: '500',
        color: '#E65100',
        margin: '0',
    },

    warningMessage: {
        fontSize: '14px',
        color: '#E65100',
        margin: '0',
        lineHeight: '1.4',
    },

    changeOverview: {
        width: '100%',
        backgroundColor: '#F8F8F8',
        borderRadius: '6px',
        marginBottom: '16px',
        padding: '12px',
    },

    changeItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '4px',

        '&:last-child': {
            marginBottom: '0',
        },
    },

    changeLabel: {
        fontSize: '14px',
        color: '#666',
        fontWeight: '500',
        minWidth: '50px',
        marginRight: '8px',
    },

    changeValue: {
        fontSize: '14px',
        color: '#0C0E29',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        flex: 1,
    },

    bundleInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
    },

    bundleName: {
        fontSize: '14px',
        color: '#0C0E29',
        fontWeight: '500',
    },

    noBundleText: {
        fontSize: '14px',
        color: '#666',
        fontStyle: 'italic',
    },

    optionsContainer: {
        width: '100%',
    },

    optionsTitle: {
        fontSize: '16px',
        fontWeight: '500',
        color: '#0C0E29',
        marginBottom: '12px',
        margin: '0 0 12px 0',
    },

    optionGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '16px',
    },

    radioOption: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        cursor: 'pointer',
        padding: '12px',
        borderRadius: '6px',
        border: '1px solid #E0E0E0',
        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#F8F8F8',
            borderColor: 'var(--color-primary)',
        },
    },

    radioIndicator: {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: '2px',
        transition: 'all 0.2s ease-in-out',
    },

    radioIndicatorSelected: {
        backgroundColor: 'var(--color-primary)',
        border: '1px solid var(--color-primary)',
    },

    radioIndicatorUnselected: {
        backgroundColor: 'transparent',
        border: '1px solid var(--color-primary)',
    },

    radioCheckIcon: {
        width: '16px',
        height: '16px',
        color: 'white',
        strokeWidth: '2px',
    },

    radioContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flex: 1,
    },

    radioTitle: {
        fontSize: '16px',
        fontWeight: '500',
        color: '#0C0E29',
    },

    radioDescription: {
        fontSize: '14px',
        color: '#666',
        lineHeight: '1.3',
    },

    datePickerContainer: {
        width: '100%',
        marginTop: '12px',
    },

    datePickerLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#0C0E29',
        marginBottom: '8px',
    },

    datePickerIcon: {
        height: '16px',
        width: '16px',
        color: '#666',
    },

    datePicker: {
        width: '100%',
        
        '& .MuiInputBase-root': {
            fontSize: '14px',
        },
        
        '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
                borderColor: 'var(--color-primary)',
            },
        },
    },

    cancelButton: {
        height: '40px',
        padding: '0 16px',
        border: '1px solid #DCDCDC',
        borderRadius: '4px',
        fontSize: '16px',
        color: '#666',
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',

        '&:hover': {
            backgroundColor: '#F8F8F8',
            borderColor: '#999',
        },
    },

    saveButton: {
        height: '40px',
        padding: '0 16px',
        border: 'none',
        borderRadius: '4px',
        fontSize: '16px',
        color: 'white',
        backgroundColor: 'var(--color-primary)',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',

        '&:hover': {
            backgroundColor: '#a77d4f',
        },
    },
}));
